import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { HelpCircle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

interface HelpEntry { title: string; intro: string; tips: string[]; }

const HELP: Record<string, HelpEntry> = {
  "/today": { title: "Shift Command", intro: "Your live operating view for the day.",
    tips: ["Open the store day to begin capturing conditions and interventions.", "Carry-forward actions from prior days appear here until resolved."] },
  "/search": { title: "Search", intro: "Find records across the platform you're allowed to see.",
    tips: ["Results are grouped by module (Stores, Users, Knowledge, Learning, Help Desk).", "You only ever see records inside your permissions and scope."] },
  "/notifications": { title: "Notifications", intro: "Your alerts plus delivery preferences.",
    tips: ["In-app alerts are always the authoritative record.", "Mute categories you don't need, or enable best-effort web push.", "Tenant admins can set per-category delivery rules under Delivery Rules."] },
  "/admin/users": { title: "Users & Roles", intro: "Invite teammates and manage access.",
    tips: ["Invitations send a one-time secure link — admins never set another user's password.", "Regenerating an invitation revokes the previous link."] },
  "/support": { title: "Help Desk", intro: "Submit and manage operational support tickets.",
    tips: ["Requesters see requester-visible updates only; internal notes stay internal.", "Agents route, assign and resolve within their queues."] },
  "/connect": { title: "Integrations (Connect)", intro: "API keys, connectors, webhooks and imports.",
    tips: ["Secrets are shown once at creation — copy them then.", "Outbound webhooks are SSRF-guarded and signed."] },
  "/intelligence": { title: "Performance & Reconciliation", intro: "Deterministic operational intelligence.",
    tips: ["Reconciliation binds accepted expectations to actuals across domains.", "Missing/stale data is shown as a data-quality state, never fabricated."] },
};

const DEFAULT_HELP: HelpEntry = {
  title: "Help", intro: "Guidance for this area of WarRoom StoreCentral.",
  tips: ["Use the search box to jump to any record you can access.", "The bell shows your notifications; open Notifications to manage preferences."],
};

function pickHelp(pathname: string): HelpEntry {
  const key = Object.keys(HELP).find((k) => pathname === k || pathname.startsWith(k + "/"));
  return key ? HELP[key] : DEFAULT_HELP;
}

export const HelpDrawer: React.FC<{ dark?: boolean }> = ({ dark }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useI18n();
  const help = pickHelp(pathname);
  return (<>
    <button
      onClick={() => setOpen(true)}
      data-testid="help-button"
      aria-label={t("common.help")}
      className={`grid h-9 w-9 place-items-center rounded-md transition-colors ${
        dark ? "text-sidebar-foreground/80 hover:bg-white/10 hover:text-white" : "text-muted-foreground hover:bg-secondary"
      }`}
    >
      <HelpCircle size={18} />
    </button>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="help-drawer" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle data-testid="help-title">{help.title}</SheetTitle>
          <SheetDescription>{help.intro}</SheetDescription>
        </SheetHeader>
        <ul className="mt-5 space-y-3">
          {help.tips.map((t, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-foreground" data-testid="help-tip">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs text-muted-foreground">{t("Need a person? Open Help Desk to raise a support ticket.")}</p>
      </SheetContent>
    </Sheet>
  </>);
};
